import { motion } from "framer-motion";
import {
  Brain,
  MessageSquareText,
  Calendar,
  BarChart3,
  Bot,
  Zap,
  Shield,
  ArrowRight,
  CheckCircle,
} from "lucide-react";

const features = [
  {
    icon: Brain,
    title: "AI Screening Agent",
    description: "Multi-dimensional scoring of resumes, LinkedIn, and GitHub to assess true capabilities. Goes beyond keywords to understand candidate potential.",
    accent: "text-accent bg-accent/5 border-accent/20",
    className: "md:col-span-2 md:row-span-1",
    stats: "94% accuracy",
    subFeatures: ["Resume parsing", "Skill taxonomy mapping", "Culture-fit scoring"],
  },
  {
    icon: Bot,
    title: "Voice Interview Agent",
    description: "Natural conversational AI that conducts screening interviews, evaluating technical depth and communication skills in real-time.",
    accent: "text-blue-500 bg-blue-500/5 border-blue-500/20",
    className: "md:col-span-1 md:row-span-1",
    stats: "10min avg",
    subFeatures: ["Natural dialogue", "Adaptive questions", "Sentiment analysis"],
  },
  {
    icon: Zap,
    title: "Automation Agent",
    description: "IF/THEN triggers that auto-advance candidates, send notifications, and schedule interviews based on AI evaluation.",
    accent: "text-amber-500 bg-amber-500/5 border-amber-500/20",
    className: "md:col-span-1 md:row-span-1",
    stats: "Zero-touch",
    subFeatures: ["Pipeline rules", "Auto-scheduling", "Smart notifications"],
  },
  {
    icon: BarChart3,
    title: "Pipeline Analytics",
    description: "Real-time visibility into hiring velocity, source quality, funnel bottlenecks, and team performance metrics.",
    accent: "text-emerald-500 bg-emerald-500/5 border-emerald-500/20",
    className: "md:col-span-1 md:row-span-1",
    stats: "Real-time",
    subFeatures: ["Funnel tracking", "Source ROI", "Team benchmarks"],
  },
  {
    icon: MessageSquareText,
    title: "AI Interview Intelligence",
    description: "Real-time scoring and behavioral feedback from every AI-conducted interview, with structured evaluation reports for hiring managers.",
    accent: "text-rose-500 bg-rose-500/5 border-rose-500/20",
    className: "md:col-span-1 md:row-span-1",
    stats: "Structured",
    subFeatures: ["Evaluation scorecards", "Skill gap analysis", "Hiring signals"],
  },
  {
    icon: Calendar,
    title: "Smart Scheduling",
    description: "Automated timezone-aware coordination with Google & Outlook calendar sync and candidate self-scheduling.",
    accent: "text-cyan-500 bg-cyan-500/5 border-cyan-500/20",
    className: "md:col-span-1 md:row-span-1",
    stats: "Auto-sync",
    subFeatures: ["Calendar integration", "Self-scheduling", "Timezone detection"],
  },
  {
    icon: Shield,
    title: "Bias-Free Hiring",
    description: "Evidence-based scoring that evaluates skills and experience, promoting equitable evaluation for every applicant.",
    accent: "text-teal-500 bg-teal-500/5 border-teal-500/20",
    className: "md:col-span-1 md:row-span-1",
    stats: "Audited",
    subFeatures: ["Fair scoring", "Explainable AI", "Transparency reports"],
  },
];

const container = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const FeaturesSection = () => {
  return (
    <section className="py-32 relative bg-white overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[radial-gradient(circle_at_center,rgba(13,148,136,0.15),rgba(255,255,255,0)_70%)] rounded-full pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 text-accent text-xs font-bold uppercase tracking-wider mb-5 border border-accent/10"
          >
            Platform
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="font-display text-4xl md:text-6xl font-bold text-slate-900 mb-6"
          >
            Everything You Need to <br /> <span className="text-accent underline decoration-accent/20 underline-offset-8">Hire at Scale</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-lg text-slate-500 max-w-2xl mx-auto font-medium"
          >
            Three autonomous AI agents plus a smart orchestration layer - all working
            24/7 to eliminate manual hiring busywork.
          </motion.p>
        </div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              variants={item}
              whileHover={{ y: -5 }}
              className={`group relative rounded-3xl p-8 bg-white border border-slate-100 hover:border-accent/20 shadow-sm hover:shadow-xl hover:shadow-accent/5 transition-all duration-300 overflow-hidden flex flex-col ${feature.className}`}
            >
              <div className="flex items-start justify-between mb-6">
                <div className={`w-14 h-14 rounded-2xl ${feature.accent} border flex items-center justify-center shadow-sm`}>
                  <feature.icon className="w-7 h-7" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-slate-50 px-2.5 py-1 rounded-full border border-slate-100">
                  {feature.stats}
                </span>
              </div>

              <h3 className="font-display font-bold text-xl text-slate-900 mb-3 flex items-center gap-2">
                {feature.title}
                <ArrowRight className="w-4 h-4 text-accent opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
              </h3>
              <p className="text-slate-500 leading-relaxed font-medium text-sm mb-5 flex-grow">
                {feature.description}
              </p>

              <div className="flex flex-wrap gap-2">
                {feature.subFeatures.map((sf) => (
                  <span
                    key={sf}
                    className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400 bg-slate-50 px-2 py-1 rounded-md border border-slate-100"
                  >
                    <CheckCircle className="w-3 h-3 text-accent" />
                    {sf}
                  </span>
                ))}
              </div>

              <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-accent/5 blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturesSection;
